import { Link } from 'react-router-dom';
import FadeIn from '@/components/FadeIn';
import { useConsultation } from '@/components/ConsultationModal';
import { IMAGES } from '@/lib/constants';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  BookOpen, BarChart3, RefreshCw, Calculator, Briefcase,
  Handshake, Check, ArrowRight, Star, CircleDollarSign
} from 'lucide-react';

const services = [
  {
    icon: BookOpen,
    title: 'Bookkeeping',
    label: 'Premier Service',
    featured: true,
    description: 'Our flagship service. Professional monthly bookkeeping that keeps your financial records accurate, organized, and current.',
    pricing: { small: '$150/mo', medium: '$350/mo', large: '$999/mo' },
    details: [
      'Monthly transaction categorization and reconciliation',
      'Bank and credit card statement reconciliation',
      'Accounts payable and receivable tracking',
      'Organized, audit-ready financial records',
      'Chart of accounts setup and maintenance',
      'Digital document organization',
    ],
  },
  {
    icon: BarChart3,
    title: 'Monthly Financial Reporting',
    description: 'Clear, actionable financial reports delivered every month so you always understand where your business stands.',
    pricing: { small: '$100/mo', medium: '$199/mo', large: '$499/mo' },
    details: [
      'Profit and loss statements',
      'Balance sheet preparation',
      'Cash flow analysis',
      'Budget vs. actual comparisons',
      'Key performance indicator tracking',
      'Executive summary with insights',
    ],
  },
  {
    icon: CircleDollarSign,
    title: 'Payroll Services',
    description: 'Reliable payroll processing that keeps your team paid on time and your business compliant. We handle the calculations, filings, and reporting so you don\'t have to.',
    pricing: { base: 'Starting at $100/mo + $10 per employee' },
    details: [
      'Payroll processing and direct deposit coordination',
      'Payroll tax calculations and filings',
      'Employee pay stub generation',
      'Year-end W-2 and 1099 preparation',
      'Payroll compliance and reporting',
      'New hire setup and onboarding support',
    ],
  },
  {
    icon: RefreshCw,
    title: 'Catch-Up / Cleanup Bookkeeping',
    description: 'Behind on your books? We bring your financial records current, clean, and organized so you can move forward with confidence. Book cleanup starts at just $200.',
    details: [
      'Book cleanup starting at $200 for small businesses',
      'Historical transaction categorization',
      'Bank reconciliation for past periods',
      'Financial record reconstruction',
      'Tax-ready preparation for previous periods',
      'Transition to organized monthly bookkeeping',
    ],
  },
  {
    icon: Calculator,
    title: 'Accounting Support',
    description: 'QuickBooks or spreadsheet tracking setup and training to get your financial systems running smoothly. We help you build the right infrastructure for your business.',
    pricing: { hourly: 'Starting at $60/hr' },
    details: [
      'QuickBooks setup and training',
      'Spreadsheet tracking system design',
      'General ledger management',
      'Financial statement preparation',
      'Accounting system optimization',
      'Financial data analysis and insights',
    ],
  },
  {
    icon: Briefcase,
    title: 'Business Consulting',
    description: 'Strategic guidance for business owners who want to strengthen operations, improve efficiency, and make smarter financial decisions.',
    pricing: { hourly: '$60/hr' },
    details: [
      'Operational efficiency assessment',
      'Financial workflow optimization',
      'Vendor and expense management',
      'Growth planning and financial strategy',
      'Business process improvement',
    ],
  },
  {
    icon: Handshake,
    title: 'CPA Coordination',
    description: 'We coordinate seamlessly with your CPA for tax preparation, compliance, and higher-level accounting needs. Need a CPA? We can connect you with ours.',
    pricing: { hourly: '$60/hr coordination', monthly: 'Use our CPA starting at $100/mo' },
    details: [
      'Year-round tax-ready bookkeeping',
      'Direct CPA communication and reporting',
      'Tax document preparation and organization',
      'Quarterly estimated tax support',
      'Audit preparation assistance',
    ],
  },
];

const tiers = [
  {
    name: 'Essential',
    label: 'Foundation',
    description: 'For small businesses needing dependable monthly bookkeeping and reporting.',
    features: [
      'Monthly bookkeeping',
      'Bank reconciliation',
      'Monthly P&L statement',
      'Balance sheet',
      'Digital document organization',
      'Email support',
    ],
  },
  {
    name: 'Growth',
    label: 'Most Popular',
    featured: true,
    description: 'For businesses needing bookkeeping plus more support, visibility, and coordination.',
    features: [
      'Everything in Essential',
      'Cash flow reporting',
      'Budget vs. actual analysis',
      'CPA coordination',
      'Quarterly business review',
      'Priority support',
      'Vendor management',
    ],
  },
  {
    name: 'Executive',
    label: 'Premium',
    description: 'For businesses needing higher-touch support, advanced reporting, and active financial organization.',
    features: [
      'Everything in Growth',
      'Weekly financial updates',
      'Custom KPI dashboard',
      'Business consulting hours',
      'Dedicated point of contact',
      'Strategic planning sessions',
      'Associate network access',
      'Same-day response guarantee',
    ],
  },
];

const faqs = [
  {
    q: 'How often will I receive financial reports?',
    a: 'All clients receive monthly financial reports including profit and loss statements and balance sheets. Growth and Executive clients receive additional reporting including cash flow analysis and budget comparisons. Executive clients can receive weekly financial updates.',
  },
  {
    q: 'What if my books are months or years behind?',
    a: 'We offer dedicated catch-up and cleanup bookkeeping services. We will reconstruct your financial records, reconcile your accounts, and bring everything current so you can start fresh with organized, accurate books.',
  },
  {
    q: 'Do you handle taxes?',
    a: 'Yes, we handle payroll tax, sales tax, and property tax. We also have a CPA we coordinate with directly, so if you are looking for a CPA for income tax preparation or other higher-level tax needs, we can connect you with a trusted professional.',
  },
  {
    q: 'What accounting software do you work with?',
    a: 'We work with Excel, QuickBooks Desktop, QuickBooks Online, and AI-powered tools. We will help you determine which software works best for your business based on your size, industry, and operational needs.',
  },
  {
    q: 'How do I get started?',
    a: 'Schedule a free consultation through our website. We will discuss your business needs, review your current financial situation, and recommend the right service plan. Most clients are fully onboarded within one to two weeks.',
    hasLink: true,
  },
  {
    q: 'Can I change my service tier later?',
    a: 'Absolutely. As your business grows or your needs change, we can adjust your service plan. Many clients start with Essential and upgrade to Growth or Executive as they see the value of additional support.',
  },
];

export default function ServicesPage() {
  const { setOpen } = useConsultation();

  return (
    <div data-testid="services-page">
      {/* HERO */}
      <section className="relative py-24 lg:py-32 bg-wc-charcoal overflow-hidden" data-testid="services-hero">
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-30 hidden lg:block">
          <img src={IMAGES.williamCorporate} alt="William K. Covington" className="w-full h-full object-cover object-top" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-wc-charcoal via-wc-charcoal/80 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <FadeIn>
            <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">Our Services</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white max-w-3xl leading-[1.1]">
              Bookkeeping and Financial Support That Drives Clarity
            </h1>
            <p className="font-body text-base lg:text-lg text-white/55 mt-6 max-w-2xl leading-relaxed">
              From monthly bookkeeping to CPA coordination, every service is designed to help you stay organized, understand your numbers, and make better business decisions.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* CLIENT CLASSIFICATION */}
      <section className="py-16 bg-wc-bg-alt border-b border-wc-border" data-testid="client-classification">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <FadeIn>
            <div className="text-center mb-10">
              <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">How We Work</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-wc-black mb-4">
                Client Classification
              </h2>
              <p className="font-body text-base text-wc-muted max-w-2xl mx-auto leading-relaxed">
                In order to meet the unique needs of your business and provide the right level of support, our clients are classified as follows:
              </p>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-px bg-wc-border">
            <FadeIn>
              <div className="bg-white p-8 lg:p-10 text-center h-full">
                <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-3">Small / Start-Up</p>
                <p className="font-heading text-2xl font-light text-wc-black mb-2">Under $100K</p>
                <p className="font-body text-sm text-wc-muted">Annual revenue less than $100,000</p>
              </div>
            </FadeIn>
            <FadeIn delay={100}>
              <div className="bg-white p-8 lg:p-10 text-center border-t-2 border-t-wc-gold h-full">
                <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-3">Medium / Growing</p>
                <p className="font-heading text-2xl font-light text-wc-black mb-2">Under $1M</p>
                <p className="font-body text-sm text-wc-muted">Annual revenue less than $1,000,000</p>
              </div>
            </FadeIn>
            <FadeIn delay={200}>
              <div className="bg-white p-8 lg:p-10 text-center h-full">
                <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-3">Large / Enterprise</p>
                <p className="font-heading text-2xl font-light text-wc-black mb-2">Over $1M</p>
                <p className="font-body text-sm text-wc-muted">Annual revenue over $1,000,000</p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* DETAILED SERVICES */}
      <section className="py-24 lg:py-32 bg-white" data-testid="services-detail">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="space-y-0">
            {services.map((service, i) => (
              <FadeIn key={i}>
                <div className={`grid lg:grid-cols-2 gap-12 lg:gap-16 py-16 ${i !== services.length - 1 ? 'border-b border-wc-border' : ''} ${service.featured ? 'relative' : ''}`}>
                  {service.featured && (
                    <div className="absolute top-16 right-0 hidden lg:block">
                      <span className="bg-wc-gold text-white font-accent text-xs uppercase tracking-[0.15em] px-4 py-1.5">{service.label}</span>
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <service.icon className="w-6 h-6 text-wc-gold" strokeWidth={1.5} />
                      {service.featured && (
                        <span className="lg:hidden bg-wc-gold text-white font-accent text-[10px] uppercase tracking-[0.15em] px-3 py-1">{service.label}</span>
                      )}
                    </div>
                    <h3 className="font-heading text-2xl sm:text-3xl font-normal text-wc-black mb-4">{service.title}</h3>
                    <p className="font-body text-base text-wc-muted leading-relaxed">{service.description}</p>
                  </div>
                  <div>
                    <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-muted mb-5">What's Included</p>
                    <ul className="space-y-3 mb-8">
                      {service.details.map((detail, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <Check className="w-4 h-4 text-wc-gold flex-shrink-0 mt-0.5" strokeWidth={2} />
                          <span className="font-body text-sm text-wc-black leading-relaxed">{detail}</span>
                        </li>
                      ))}
                    </ul>
                    {service.pricing && (
                      <div className="border border-wc-border p-6 bg-wc-bg-alt">
                        <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">Pricing</p>
                        {service.pricing.small && (
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="font-body text-sm text-wc-muted">Small / Start-Up</span>
                              <span className="font-body text-sm font-semibold text-wc-black">{service.pricing.small}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="font-body text-sm text-wc-muted">Medium / Growing</span>
                              <span className="font-body text-sm font-semibold text-wc-black">{service.pricing.medium}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="font-body text-sm text-wc-muted">Large / Enterprise</span>
                              <span className="font-body text-sm font-semibold text-wc-black">{service.pricing.large}</span>
                            </div>
                          </div>
                        )}
                        {service.pricing.base && (
                          <p className="font-body text-sm font-semibold text-wc-black">{service.pricing.base}</p>
                        )}
                        {service.pricing.hourly && (
                          <p className="font-body text-sm font-semibold text-wc-black">{service.pricing.hourly}</p>
                        )}
                        {service.pricing.monthly && (
                          <p className="font-body text-sm font-semibold text-wc-black mt-2">{service.pricing.monthly}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING TIERS */}
      <section className="py-24 lg:py-32 bg-wc-bg-alt" data-testid="services-tiers">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">Service Plans</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-wc-black mb-4">
                Choose Your Level of Support
              </h2>
              <p className="font-body text-base text-wc-muted max-w-2xl mx-auto leading-relaxed">
                Every plan includes professional bookkeeping. Select the tier that matches your business needs and growth goals.
              </p>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-px bg-wc-border">
            {tiers.map((tier, i) => (
              <FadeIn key={i} delay={i * 100}>
                <div className={`bg-white p-10 lg:p-12 h-full flex flex-col ${tier.featured ? 'border-t-2 border-t-wc-gold' : ''}`}>
                  <div>
                    <span className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold">{tier.label}</span>
                    <h3 className="font-heading text-2xl sm:text-3xl font-normal text-wc-black mt-3 mb-3">{tier.name}</h3>
                    <p className="font-body text-sm text-wc-muted leading-relaxed mb-8">{tier.description}</p>
                  </div>
                  <ul className="space-y-3 mb-10 flex-1">
                    {tier.features.map((feature, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <Check className="w-4 h-4 text-wc-gold flex-shrink-0 mt-0.5" strokeWidth={2} />
                        <span className="font-body text-sm text-wc-black">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => setOpen(true)}
                    className={`w-full py-3.5 font-body text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                      tier.featured
                        ? 'bg-wc-gold text-white hover:bg-wc-gold-hover'
                        : 'bg-wc-black text-white hover:bg-wc-charcoal'
                    }`}
                    data-testid={`tier-cta-${tier.name.toLowerCase()}`}
                  >
                    Get Started
                  </button>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-32 bg-white" data-testid="services-faq">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">FAQ</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-wc-black">
                Common Questions
              </h2>
            </div>
          </FadeIn>
          <FadeIn>
            <Accordion type="single" collapsible className="space-y-0" data-testid="faq-accordion">
              {faqs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-b border-wc-border">
                  <AccordionTrigger className="font-body text-base text-wc-black hover:text-wc-gold py-6 text-left hover:no-underline" data-testid={`faq-trigger-${i}`}>
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="font-body text-sm text-wc-muted leading-relaxed pb-6">
                    {faq.a}
                    {faq.hasLink && (
                      <button
                        onClick={() => setOpen(true)}
                        className="inline-block ml-1 text-wc-gold hover:text-wc-gold-hover underline underline-offset-2 transition-colors duration-300"
                        data-testid="faq-consultation-link"
                      >
                        Schedule your free consultation here.
                      </button>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeIn>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-wc-black" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <FadeIn>
            <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-6">Ready to Start?</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-white mb-6">
              Let's Get Your Books Organized
            </h2>
            <p className="font-body text-base lg:text-lg text-white/55 max-w-xl mx-auto mb-10 leading-relaxed">
              Schedule a consultation and we'll recommend the right service plan for your business.
            </p>
            <button
              onClick={() => setOpen(true)}
              className="bg-wc-gold text-white px-10 py-4 font-body text-sm uppercase tracking-[0.15em] hover:bg-wc-gold-hover transition-colors duration-300"
              data-testid="services-cta-button"
            >
              Schedule a Consultation
            </button>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
